import {
  ACCOUNTS_GRID_H,
  OPEN_ORDER_GRID_H,
} from '../../constants/grid.layout';

export const GRID_ROW_HEIGHT = 20;
export const GRID_MARGIN_X = 8;
export const GRID_MARGIN_Y = 8;

const VIEWPORT_FILL_PANELS = {
  OrderBook: { minH: 20 },
  OpenOrder: { minH: OPEN_ORDER_GRID_H },
  Accounts: { minH: ACCOUNTS_GRID_H },
  MarketTrades: { minH: 11 },
};

/** Largest grid `h` that fits within the pixel budget (avoids main-page scroll). */
export function pixelHeightToGridUnitsFloor(pixelHeight) {
  if (!Number.isFinite(pixelHeight) || pixelHeight <= 0) return 1;

  let h = Math.floor((pixelHeight + GRID_MARGIN_Y) / (GRID_ROW_HEIGHT + GRID_MARGIN_Y));
  while (h > 1 && gridUnitsToPixelHeight(h) > pixelHeight) {
    h -= 1;
  }
  while (gridUnitsToPixelHeight(h + 1) <= pixelHeight) {
    h += 1;
  }
  return Math.max(1, h);
}

/**
 * Stretch panels to the viewport bottom row. Runtime-only for default auto-fit;
 * do not persist — user drag/resize disables auto-fit and saves their layout.
 */
export function applyViewportBottomFill(layouts, fillBottomGridH) {
  if (!fillBottomGridH || !layouts) return layouts;

  const patchItems = (items) => {
    if (!items?.length) return items;

    return items.map((item) => {
      const config = VIEWPORT_FILL_PANELS[item.i];
      if (!config) return { ...item };

      const y = Number(item.y) || 0;
      const nextH = Math.max(config.minH, fillBottomGridH - y);
      return { ...item, h: nextH };
    });
  };

  return {
    ...layouts,
    lg: layouts.lg ? patchItems(layouts.lg) : layouts.lg,
    md: layouts.md ? patchItems(layouts.md) : layouts.md,
  };
}

/** Deep-clone layout maps so react-grid-layout can mutate x/y/w/h during drag. */
export function cloneGridLayouts(layouts) {
  if (!layouts) return layouts;
  try {
    return JSON.parse(JSON.stringify(layouts));
  } catch {
    return layouts;
  }
}

/** Convert pixel height to react-grid-layout `h` units. */
export function pixelHeightToGridUnits(pixelHeight) {
  if (!Number.isFinite(pixelHeight) || pixelHeight <= 0) return 1;
  return Math.max(1, Math.ceil((pixelHeight + GRID_MARGIN_Y) / (GRID_ROW_HEIGHT + GRID_MARGIN_Y)));
}

export function gridUnitsToPixelHeight(h) {
  if (!Number.isFinite(h) || h <= 0) return GRID_ROW_HEIGHT;
  return h * GRID_ROW_HEIGHT + Math.max(0, h - 1) * GRID_MARGIN_Y;
}

const patchBreakpointItems = (items, gridH) => {
  if (!items?.length) return items;

  const buyItem = items.find((item) => item.i === 'BuySellTab');
  if (!buyItem) return items;

  const nextY = buyItem.y + gridH;
  return items.map((item) => {
    if (item.i === 'BuySellTab') return { ...item, h: gridH };
    if (item.i === 'Accounts') return { ...item, y: nextY };
    // Always return a new object — RGL mutates layout items in place.
    return { ...item };
  });
};

/** Apply measured Place Order height and shift Accounts below it. */
export function applyBuySellTabAutoHeight(layouts, gridH) {
  if (!gridH || !layouts) return layouts;

  return {
    ...layouts,
    lg: patchBreakpointItems(layouts.lg, gridH),
    md: patchBreakpointItems(layouts.md, gridH),
  };
}
